import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { InfoIcon, RightArrowFilledIcon } from "../../../../assets/Icons";
import { remittanceActions } from "../../state/remittanceSlice";
import {
  AppConstants,
  AppStrings,
  isEducationTransactionPurpose,
  logApiErrorToSentry,
  PINCODE_MAX_LENGTH,
  hasInvalidPincodeCharacters,
  isAllowedPincodeKey,
  purposeValEnhancer,
  sanitizeRecipientPincode,
  validateRecipientPincode,
} from "../../utils";
import SelectDropdown from "../../components/ui/SelectDropdown";
import { DebounceInput } from "react-debounce-input";
import Button from "../../../../components/ui/Button";
import SwiftIbanTab from "../../components/ui/SwiftIbanTab";
import { remittanceApi } from "../../api/remittanceApi";
import Checkbox from "../../../../components/ui/Checkbox";

const RemittanceAddRecipient: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const remitterState = useAppSelector((state) => state.remittance);
  const { calculator, remitter, recipient, addrecipient } = useAppSelector(
    (state) => state.remittance
  );

  const [isCorrespondingAccount, setIsCorrespondingAccount] = useState(false);
  const [pincodeSpecialCharError, setPincodeSpecialCharError] = useState(false);

  const isEducationPurpose = isEducationTransactionPurpose(
    calculator?.amountCalculation?.transaction_purpose,
    calculator?.amountCalculation?.purposeCode
  );

  const pincodeValidation = validateRecipientPincode(addrecipient?.recipientInput?.step2?.pincode);

  const step1Disable =
    addrecipient?.recipientCategoryList?.loading ||
    !addrecipient?.recipientInput?.step1?.recipientName ||
    !addrecipient?.recipientInput?.step1?.recipientCategoryDropdown;

  const step2Disable =
    (addrecipient?.recipientInput?.step1?.recipientCategoryDropdown === "Individual" &&
      !addrecipient?.recipientInput?.step2?.relationship) ||
    !addrecipient?.recipientInput?.step2?.country ||
    !addrecipient?.recipientInput?.step2?.city ||
    !addrecipient?.recipientInput?.step2?.pincode ||
    !pincodeValidation.isValid ||
    !addrecipient?.recipientInput?.step2?.addressLine1;

  const isSortCodeRequired =
    addrecipient?.recipientInput?.step3?.sortBSBTransitRoutingPlaceholder !==
    "Sort/BSB/Transit/Routing(Optional)";
  const isSortCodeInvalid =
    isSortCodeRequired && !addrecipient?.recipientInput?.step3?.sortBSBTransitRouting;

  const step3Disable =
    !addrecipient?.recipientInput?.step3?.bankAccNo ||
    !addrecipient?.recipientInput?.step3?.confirmBankAccNo ||
    (addrecipient?.recipientInput?.step3?.confirmBankAccNo !==
      addrecipient?.recipientInput?.step3?.bankAccNo &&
      addrecipient?.recipientInput?.step3?.confirmBankAccNo) ||
    // !addrecipient?.recipientInput?.step3?.bankAccCountry ||  commented for removing mandatory field
    !addrecipient?.recipientInput?.step3?.bankName ||
    isSortCodeInvalid ||
    !addrecipient?.recipientInput?.step3?.swiftIbanVerified;

  const step4Disable =
    !addrecipient?.recipientInput?.step4?.bankAccNo ||
    !addrecipient?.recipientInput?.step4?.confirmBankAccNo ||
    (addrecipient?.recipientInput?.step4?.confirmBankAccNo !==
      addrecipient?.recipientInput?.step4?.bankAccNo &&
      addrecipient?.recipientInput?.step4?.confirmBankAccNo) ||
    !addrecipient?.recipientInput?.step4?.bankName;

  {
    /* Initial API call and condition when user came to this page */
  }
  useEffect(() => {
    dispatch(
      remittanceActions.fetchRecipientCategoryListRequested({
        purposeCode: purposeValEnhancer(calculator?.amountCalculation?.transaction_purpose),
        currency: calculator?.amountCalculation?.dest_currency,
      })
    );
  }, []);

  const getBankCountry = (countryName?: string): string => {
    switch (countryName) {
      case "AUSTRALIA":
        return "Australia";
      case "CANADA":
        return "Canada";
      case "UNITED KINGDOM":
        return "United Kingdom";
      case "UNITED STATES OF AMERICA":
        return "United States";
      default:
        return countryName ?? "";
    }
  };

  const onVerifySwiftOrIban = async () => {
    try {
      dispatch(
        remittanceActions.setAddRecipientInput({
          step3: {
            ...addrecipient?.recipientInput?.step3,
            swiftIbanLoading: true,
            swiftIbanError: null,
          },
        })
      );
      const response = await remittanceApi.getValidRecipientSwiftOrIban(
        addrecipient?.recipientInput?.step3?.isSwift ? "swift" : "iban",
        addrecipient?.recipientInput?.step3?.swiftOrIbanNumber
      );
      if (addrecipient?.recipientInput?.step3?.isSwift) {
        dispatch(
          remittanceActions.setAddRecipientInput({
            step3: {
              ...addrecipient?.recipientInput?.step3,
              swiftIbanLoading: false,
              swiftIbanError: null,
              swiftIbanVerified: true,
              bankAccNo: "",
              confirmBankAccNo: "",
              bankAccCountry: getBankCountry(response?.data?.country_name),
              bankName: response?.data?.name,
              sortBSBTransitRouting: response?.data?.branch_code,
              bankAddress: response?.data?.address,
            },
          })
        );
      } else {
        dispatch(
          remittanceActions.setAddRecipientInput({
            step3: {
              ...addrecipient?.recipientInput?.step3,
              swiftIbanLoading: false,
              swiftIbanError: null,
              swiftIbanVerified: true,
              bankAccNo: response?.data?.account,
              confirmBankAccNo: response?.data?.account,
              bankAccCountry: getBankCountry(response?.data?.country),
              bankName: response?.data?.bank == "null" ? "" : response?.data?.bank,
              sortBSBTransitRouting: response?.data?.branch_code,
              bankAddress: response?.data?.address,
            },
          })
        );
      }
    } catch (err: any) {
      const errorMsg =
        err?.response?.data?.error?.type ||
        err?.response?.error?.type ||
        err?.response?.error ||
        AppStrings.GENERIC_ERROR;
      dispatch(
        remittanceActions.setAddRecipientInput({
          step3: {
            ...addrecipient?.recipientInput?.step3,
            swiftIbanLoading: false,
            swiftIbanError: errorMsg,
          },
        })
      );
      logApiErrorToSentry(err, "onVerifySwiftOrIban");
    }
  };

  useEffect(() => {
    if (AppConstants.IbanCountryArray.includes(addrecipient?.recipientInput?.step2?.country)) {
      dispatch(
        remittanceActions.setAddRecipientInput({
          step3: {
            ...addrecipient?.recipientInput?.step3,
            swiftIbanLoading: false,
            swiftIbanError: null,
            swiftIbanVerified: false,
            isSwift: false,
          },
        })
      );
    }
  }, [addrecipient?.recipientInput?.step2?.country]);

  useEffect(() => {
    if (AppConstants.codeCountry.includes(addrecipient?.recipientInput?.step3?.bankAccCountry)) {
      dispatch(
        remittanceActions.setAddRecipientInput({
          step3: {
            ...addrecipient?.recipientInput?.step3,
            sortBSBTransitRoutingPlaceholder: getBankCodeLabel(
              addrecipient?.recipientInput?.step3?.bankAccCountry
            ),
          },
        })
      );
    } else {
      dispatch(
        remittanceActions.setAddRecipientInput({
          step3: {
            ...addrecipient?.recipientInput?.step3,
            sortBSBTransitRoutingPlaceholder: "Sort/BSB/Transit/Routing(Optional)",
            sortBSBTransitRouting: "",
          },
        })
      );
    }
  }, [addrecipient?.recipientInput?.step3?.bankAccCountry]);

  const getBankCodeLabel = (bankCountry: string) => {
    switch (bankCountry) {
      case "United States":
        return "Routing Number";
      case "Canada":
        return "Transit Code";
      case "Australia":
        return "BSB Code";
      case "United Kingdom":
        return "Sort Code";
      default:
        return "Sort/BSB/Transit/Routing (Optional)";
    }
  };

  const handleAddRecipient = () => {
    const body = {
      firstName: addrecipient?.recipientInput?.step1?.recipientName,
      middleName: "",
      nickName: "",
      lastName: "",
      relationship: addrecipient?.recipientInput?.step2?.relationship,
      bankName: addrecipient?.recipientInput?.step3?.bankName,
      bankAddress: addrecipient?.recipientInput?.step3?.bankAddress,
      swiftCode: addrecipient?.recipientInput?.step3?.isSwift
        ? addrecipient?.recipientInput?.step3?.swiftOrIbanNumber
        : "",
      ibanCode: addrecipient?.recipientInput?.step3?.isSwift
        ? ""
        : addrecipient?.recipientInput?.step3?.swiftOrIbanNumber,
      achCode:
        addrecipient?.recipientInput?.step3?.bankAccCountry == "United States"
          ? addrecipient?.recipientInput?.step3?.sortBSBTransitRouting
          : "",
      transitCode:
        addrecipient?.recipientInput?.step3?.bankAccCountry == "Canada"
          ? addrecipient?.recipientInput?.step3?.sortBSBTransitRouting
          : "",
      bsbCode:
        addrecipient?.recipientInput?.step3?.bankAccCountry == "Australia"
          ? addrecipient?.recipientInput?.step3?.sortBSBTransitRouting
          : "",
      sortCode:
        addrecipient?.recipientInput?.step3?.bankAccCountry == "United Kingdom"
          ? addrecipient?.recipientInput?.step3?.sortBSBTransitRouting
          : "",
      unitNumber: addrecipient?.recipientInput?.step3?.unitNumber,
      bankAccountNumber: addrecipient?.recipientInput?.step3?.bankAccNo,
      countryOfResidence: addrecipient?.recipientInput?.step2?.country,
      recipientAddress:
        addrecipient?.recipientInput?.step2?.addressLine1 +
        addrecipient?.recipientInput?.step2?.addressLine2,
      city: addrecipient?.recipientInput?.step2?.city,
      phoneNumber: "",
      email: "",
      pinCode: addrecipient?.recipientInput?.step2?.pincode,
      state: "",
      recipientLs: "",
      bankAccountCountry: addrecipient?.recipientInput?.step3?.bankAccCountry,
      currency: "inr",
      refrence: addrecipient?.recipientInput?.step3?.referenceId,
      ifscCode: "",
      bankCode: "",
      type: addrecipient?.recipientInput?.step1?.recipientCategoryDropdown,
      correspondingBankName: addrecipient?.recipientInput?.step3?.correspondingAccount
        ? addrecipient?.recipientInput?.step4?.bankName
        : null,
      correspondingBankAccountNumber: addrecipient?.recipientInput?.step3?.correspondingAccount
        ? addrecipient?.recipientInput?.step4?.bankAccNo
        : null,
      ...(isEducationPurpose
        ? { studentName: addrecipient?.recipientInput?.step1?.studentName }
        : {}),
    };
    dispatch(remittanceActions.setAddRecipientSubmit({ loading: true, error: null }));
    remittanceApi
      .postAddRecipientByRemitter(remitter?.selectedRemitter?.userKey, body)
      .then((response) => {
        dispatch(remittanceActions.setAddRecipientSubmit({ loading: false, data: response?.data }));
        toast.success("Recipient added successfully");
        navigate("/send/recipient");
      })
      .catch((err) => {
        const errorMsg =
          err?.response?.data?.error?.type ||
          err?.response?.error?.type ||
          err?.response?.error ||
          AppStrings.GENERIC_ERROR;
        dispatch(remittanceActions.setAddRecipientSubmit({ loading: false, error: errorMsg }));
        toast.error(errorMsg);
        logApiErrorToSentry(err, "handleAddRecipient");
      });
  };

  useEffect(() => {
    if (remitterState?.progresStep?.activeStepNumber < 3) {
      navigate("/send/calculator", { replace: true });
    }
  }, [remitterState?.progresStep?.activeStepNumber]);

  useEffect(() => {
    const handleBeforeUnload = (event: any) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  const steps = [
    { label: "Recipient Details" },
    { label: "Recipient Address" },
    { label: "Recipient Bank Details" },
    addrecipient?.recipientInput?.step3?.correspondingAccount && {
      label: "Corresponding Bank Account",
    },
  ].filter(Boolean);

  return (
    <>
      <div className="max-w-[1440px] w-full mx-auto px-[160px]">
        <ToastContainer autoClose={3000} position="top-right" />

        {/* Progress Steps */}
        <div id="stepnav" className="flex items-center justify-center mt-2 gap-4">
          {steps.map((stepItem: any, index: number) => {
            const isActive = addrecipient?.recipientStepNumber === index;
            const isCompleted = addrecipient?.recipientStepNumber > index;
            return (
              <React.Fragment key={index}>
                <div
                  className={`
                    flex flex-col px-4 py-2 rounded-lg min-w-[160px]
                    transition-all
                    ${
                      isActive
                        ? "bg-blue-50 border border-blue-200"
                        : "bg-white border border-gray-200"
                    }
                  `}
                >
                  <span
                    className={`text-xs font-semibold ${
                      isActive ? "text-blue-600" : "text-gray-400"
                    }`}
                  >
                    Step {index + 1}
                  </span>

                  <span
                    className={`text-sm ${
                      isActive ? "font-medium text-gray-900" : "text-gray-600"
                    }`}
                  >
                    {stepItem.label}
                  </span>
                </div>

                {index < steps.length - 1 && (
                  <RightArrowFilledIcon
                    height="14"
                    width="14"
                    fill={isCompleted ? "#3b82f6" : "#d1d5db"}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Step 1 */}
        {addrecipient?.recipientStepNumber == 0 && (
          <div className="flex items-center justify-center flex-col mt-10">
            <div className="w-[400px]">
              <span className="block text-black-100 text-left text-[15px]">
                Recipient Name
                <span className="text-red-100 text-sm">*</span>
              </span>
            </div>
            <DebounceInput
              className={"w-[400px] rounded-md h-14 p-4 bg-[#fff] border text-base outline-none"}
              name="recipientName"
              type="text"
              value={addrecipient?.recipientInput?.step1?.recipientName}
              debounceTimeout={0}
              onChange={(e: any) => {
                const value = e?.target?.value?.trim() || "";
                dispatch(
                  remittanceActions.setAddRecipientInput({
                    step1: {
                      ...addrecipient?.recipientInput?.step1,
                      recipientName: value,
                    },
                  })
                );
              }}
              fullWidth
              autoComplete="off"
              inputClasses="text-[20px] bg-blue-100"
              disabled={false}
              placeholder={""}
            />
            <div className="w-[400px] mt-5">
              <span className="block text-black-100 text-left text-[15px]">
                Recipient Category
                <span className="text-red-100 text-sm">*</span>
              </span>
            </div>
            <SelectDropdown
              data={addrecipient?.recipientCategoryList?.list}
              value={addrecipient?.recipientInput?.step1?.recipientCategoryDropdown}
              onChange={(response) => {
                dispatch(
                  remittanceActions.setAddRecipientInput({
                    step1: {
                      ...addrecipient?.recipientInput?.step1,
                      recipientCategoryDropdown: response,
                    },
                  })
                );
              }}
              getLabel={(item) => item}
              getValue={(item) => item}
              width={"400px"}
              placeHolderFontSize={"16px"}
              placeHolderColor={"#a6a6a6"}
              textSize={"16px"}
              fontWeight={400}
              backgroundColor="#fff"
              isSearchable={true}
              isLoading={addrecipient?.recipientCategoryList?.loading}
            />

            {isEducationPurpose && (
              <>
                <div className="w-[400px] mt-5">
                  <span className="block text-black-100 text-left text-[15px]">
                    Student Name(Optional)
                  </span>
                </div>
                <DebounceInput
                  className={
                    "w-[400px] rounded-md h-14 p-4 bg-[#fff] border text-base outline-none"
                  }
                  name="studentName"
                  type="text"
                  value={addrecipient?.recipientInput?.step1?.studentName}
                  debounceTimeout={0}
                  onChange={(e: any) => {
                    const value = e?.target?.value?.trim() || "";
                    dispatch(
                      remittanceActions.setAddRecipientInput({
                        step1: {
                          ...addrecipient?.recipientInput?.step1,
                          studentName: value,
                        },
                      })
                    );
                  }}
                  fullWidth
                  autoComplete="off"
                  inputClasses="text-[20px] bg-blue-100"
                  disabled={false}
                  placeholder={""}
                />
              </>
            )}
            <div className="flex items-center justify-center align-middle  mt-8">
              <Button
                bgColor={step1Disable ? "bg-gray-300" : "bg-[#006EFF]"}
                fontStyle={{
                  fontFamily: "font-Inter",
                  fontSize: "text-sm",
                  fontWeight: "font-bold",
                  fontColor: "text-white",
                }}
                borderStyle={{ borderRadius: "rounded-lg" }}
                className={`p-4 ${!step1Disable ? "hover:bg-blue-600" : ""} w-[10rem]`}
                onClickHandler={() => {
                  dispatch(remittanceActions.setRecipientStepNumber(1));
                }}
                disabled={step1Disable}
                isLoading={false}
              >
                <div className="flex items-center gap-1">{"Proceed"}</div>
              </Button>
            </div>
          </div>
        )}

        {/* Step 2 */}
        {addrecipient?.recipientStepNumber == 1 && (
          <div className="flex items-center justify-center flex-col mt-10">
            <div className="flex gap-5">
              <div>
                <div className="w-[400px]">
                  <span className="block text-black-100 text-left text-[15px]">
                    Relationship
                    <span className="text-red-100 text-sm">
                      {addrecipient?.recipientInput?.step1?.recipientCategoryDropdown !==
                      "Individual"
                        ? ""
                        : "*"}
                    </span>
                  </span>
                </div>
                <SelectDropdown
                  data={AppConstants.RelationshipsList}
                  value={addrecipient?.recipientInput?.step2?.relationship}
                  onChange={(response) => {
                    dispatch(
                      remittanceActions.setAddRecipientInput({
                        step2: {
                          ...addrecipient?.recipientInput?.step2,
                          relationship: response,
                        },
                      })
                    );
                  }}
                  getLabel={(item) => item}
                  getValue={(item) => item}
                  width={"400px"}
                  placeHolderFontSize={"16px"}
                  placeHolderColor={"#a6a6a6"}
                  textSize={"16px"}
                  fontWeight={400}
                  backgroundColor={
                    addrecipient?.recipientInput?.step1?.recipientCategoryDropdown !== "Individual"
                      ? "#f2f2f2"
                      : "#fff"
                  }
                  isSearchable={true}
                  isLoading={false}
                  isDisabled={
                    addrecipient?.recipientInput?.step1?.recipientCategoryDropdown !== "Individual"
                  }
                />
              </div>
              <div>
                <div className="w-[400px]">
                  <span className="block text-black-100 text-left text-[15px]">
                    Country of Residence
                    <span className="text-red-100 text-sm">*</span>
                  </span>
                </div>
                <SelectDropdown
                  data={AppConstants.CountryOfResidence}
                  value={addrecipient?.recipientInput?.step2?.country}
                  onChange={(response) => {
                    dispatch(
                      remittanceActions.setAddRecipientInput({
                        step2: {
                          ...addrecipient?.recipientInput?.step2,
                          country: response,
                        },
                        step3: {
                          ...addrecipient?.recipientInput?.step3,
                          swiftOrIbanNumber: "",
                          swiftIbanVerified: false,
                        },
                      })
                    );
                  }}
                  getLabel={(item) => item}
                  getValue={(item) => item}
                  width={"400px"}
                  placeHolderFontSize={"16px"}
                  placeHolderColor={"#a6a6a6"}
                  textSize={"16px"}
                  fontWeight={400}
                  backgroundColor="#fff"
                  isSearchable={true}
                  isLoading={addrecipient?.recipientCategoryList?.loading}
                />
              </div>
            </div>
            <div className="flex gap-5 mt-5">
              <div>
                <div className="w-[400px]">
                  <span className="block text-black-100 text-left text-[15px]">
                    City
                    <span className="text-red-100 text-sm">*</span>
                  </span>
                </div>
                <DebounceInput
                  className={
                    "w-[400px] rounded-md h-14 p-4 bg-[#fff] border text-base outline-none"
                  }
                  name="city"
                  type="text"
                  value={addrecipient?.recipientInput?.step2?.city}
                  debounceTimeout={0}
                  onChange={(e: any) => {
                    const value = e?.target?.value?.trim() || "";
                    dispatch(
                      remittanceActions.setAddRecipientInput({
                        step2: {
                          ...addrecipient?.recipientInput?.step2,
                          city: value,
                        },
                      })
                    );
                  }}
                  fullWidth
                  autoComplete="off"
                  inputClasses="text-[20px] bg-blue-100"
                  disabled={false}
                  placeholder={""}
                />
              </div>
              <div>
                <div className="w-[400px]">
                  <span className="block text-black-100 text-left text-[15px]">
                    PIN code
                    <span className="text-red-100 text-sm">*</span>
                  </span>
                </div>
                <DebounceInput
                  className={
                    "w-[400px] rounded-md h-14 p-4 bg-[#fff] border text-base outline-none"
                  }
                  name="pincode"
                  type="text"
                  value={addrecipient?.recipientInput?.step2?.pincode}
                  debounceTimeout={0}
                  maxLength={PINCODE_MAX_LENGTH}
                  onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    const withModifier = e.metaKey || e.ctrlKey || e.altKey;
                    if (!isAllowedPincodeKey(e.key, withModifier)) {
                      e.preventDefault();
                    }
                  }}
                  onPaste={(e: React.ClipboardEvent<HTMLInputElement>) => {
                    const pasted = e.clipboardData.getData("text");
                    if (hasInvalidPincodeCharacters(pasted)) {
                      e.preventDefault();
                      const current = addrecipient?.recipientInput?.step2?.pincode ?? "";
                      const next = sanitizeRecipientPincode(`${current}${pasted}`);
                      dispatch(
                        remittanceActions.setAddRecipientInput({
                          step2: {
                            ...addrecipient?.recipientInput?.step2,
                            pincode: next,
                          },
                        })
                      );
                    }
                  }}
                  onChange={(e: any) => {
                    const value = sanitizeRecipientPincode(e?.target?.value);
                    dispatch(
                      remittanceActions.setAddRecipientInput({
                        step2: {
                          ...addrecipient?.recipientInput?.step2,
                          pincode: value,
                        },
                      })
                    );
                  }}
                  autoComplete="off"
                  fullWidth
                  inputClasses="text-[20px] bg-blue-100"
                  disabled={false}
                  placeholder={""}
                />
                {!!addrecipient?.recipientInput?.step2?.pincode && !pincodeValidation.isValid && (
                  <span className="block text-red-100 text-sm mt-1 max-w-[400px]">
                    {pincodeValidation.error}
                  </span>
                )}
              </div>
            </div>
            <div className="flex gap-5 mt-5">
              <div>
                <div className="w-[400px]">
                  <span className="block text-black-100 text-left text-[15px]">
                    Address Line 1<span className="text-red-100 text-sm">*</span>
                  </span>
                </div>
                <DebounceInput
                  className={
                    "w-[400px] rounded-md h-14 p-4 bg-[#fff] border text-base outline-none"
                  }
                  name="addressLine1"
                  type="text"
                  value={addrecipient?.recipientInput?.step2?.addressLine1}
                  debounceTimeout={0}
                  onChange={(e: any) => {
                    const value = e?.target?.value?.trim() || "";
                    dispatch(
                      remittanceActions.setAddRecipientInput({
                        step2: {
                          ...addrecipient?.recipientInput?.step2,
                          addressLine1: value,
                        },
                      })
                    );
                  }}
                  fullWidth
                  autoComplete="off"
                  inputClasses="text-[20px] bg-blue-100"
                  disabled={false}
                  placeholder={""}
                />
              </div>
              <div>
                <div className="w-[400px]">
                  <span className="block text-black-100 text-left text-[15px]">
                    Address Line 2<span className="text-red-100 text-sm"></span>
                  </span>
                </div>
                <DebounceInput
                  className={
                    "w-[400px] rounded-md h-14 p-4 bg-[#fff] border text-base outline-none"
                  }
                  name="addressLine2"
                  type="text"
                  value={addrecipient?.recipientInput?.step2?.addressLine2}
                  debounceTimeout={0}
                  onChange={(e: any) => {
                    const value = e?.target?.value?.trim() || "";
                    dispatch(
                      remittanceActions.setAddRecipientInput({
                        step2: {
                          ...addrecipient?.recipientInput?.step2,
                          addressLine2: value,
                        },
                      })
                    );
                  }}
                  autoComplete="off"
                  fullWidth
                  inputClasses="text-[20px] bg-blue-100"
                  disabled={false}
                  placeholder={""}
                />
              </div>
            </div>
            <div className="flex items-center justify-center align-middle mt-8 gap-5">
              <Button
                bgColor={"bg-white"}
                fontStyle={{
                  fontFamily: "font-Inter",
                  fontSize: "text-sm",
                  fontWeight: "font-bold",
                  fontColor: "text-white",
                }}
                borderStyle={{
                  borderRadius: "rounded-lg",
                  borderWidth: "border",
                  borderColor: "border-gray-300",
                }}
                className={"p-4 hover:bg-gray-50 w-[10rem] h-12"}
                onClickHandler={() => {
                  dispatch(remittanceActions.setRecipientStepNumber(0));
                }}
                disabled={false}
                isLoading={false}
              >
                <div className="flex items-center justify-center gap-1 text-blue-600">
                  {"Go Back"}
                </div>
              </Button>
              <Button
                bgColor={step2Disable ? "bg-gray-300" : "bg-[#006EFF]"}
                fontStyle={{
                  fontFamily: "font-Inter",
                  fontSize: "text-sm",
                  fontWeight: "font-bold",
                  fontColor: "text-white",
                }}
                borderStyle={{ borderRadius: "rounded-lg" }}
                className={`p-4 ${!step2Disable ? "hover:bg-blue-600" : ""} w-[10rem]`}
                onClickHandler={() => {
                  dispatch(remittanceActions.setRecipientStepNumber(2));
                }}
                disabled={step2Disable}
                isLoading={false}
              >
                <div className="flex items-center gap-1">{"Proceed"}</div>
              </Button>
            </div>
          </div>
        )}

        {/* Step 3 */}
        {addrecipient?.recipientStepNumber == 2 && (
          <div className="flex items-center justify-center flex-col mt-10">
            {!AppConstants.IbanCountryArray.includes(
              addrecipient?.recipientInput?.step2?.country
            ) && (
              <div className="w-full flex justify-center">
                <div className="flex flex-col items-start">
                  <span className="text-black-100 text-[15px] mb-1">
                    Please choose SWIFT or IBAN
                  </span>
                  <SwiftIbanTab
                    isSwift={addrecipient?.recipientInput?.step3?.isSwift}
                    setIsSwift={(flag) => {
                      dispatch(
                        remittanceActions.setAddRecipientInput({
                          step3: {
                            ...addrecipient?.recipientInput?.step3,
                            isSwift: flag,
                          },
                        })
                      );
                      if (addrecipient?.recipientInput?.step3?.isSwift !== flag) {
                        dispatch(
                          remittanceActions.setAddRecipientInput({
                            step3: {
                              ...addrecipient?.recipientInput?.step3,
                              isSwift: flag,
                              swiftIbanError: null,
                            },
                          })
                        );
                      }
                    }}
                    isVerified={addrecipient?.recipientInput?.step3?.swiftIbanVerified}
                  />
                </div>
              </div>
            )}
            <div className="gap-5 mt-8 flex">
              <DebounceInput
                className={"w-[650px] rounded-md h-14 p-4 bg-[#fff] border text-base outline-none"}
                name="swiftOrIbanCode"
                type="text"
                value={addrecipient?.recipientInput?.step3?.swiftOrIbanNumber}
                debounceTimeout={0}
                onChange={(e: any) => {
                  const value = e?.target?.value?.trim() || "";
                  dispatch(
                    remittanceActions.setAddRecipientInput({
                      step3: {
                        ...addrecipient?.recipientInput?.step3,
                        swiftOrIbanNumber: value,
                      },
                    })
                  );
                }}
                autoComplete="off"
                fullWidth
                inputClasses="text-[20px] bg-blue-100"
                disabled={addrecipient?.recipientInput?.step3?.swiftIbanVerified}
                placeholder={
                  addrecipient?.recipientInput?.step3?.isSwift ? "Swift code" : "IBAN number"
                }
              />
              <Button
                bgColor={
                  !addrecipient?.recipientInput?.step3?.swiftOrIbanNumber
                    ? "bg-gray-300"
                    : addrecipient?.recipientInput?.step3?.swiftIbanVerified
                    ? "bg-[#37C820]"
                    : "bg-[#006EFF]"
                }
                fontStyle={{
                  fontFamily: "font-Inter",
                  fontSize: "text-sm",
                  fontWeight: "font-bold",
                  fontColor: "text-white",
                }}
                borderStyle={{ borderRadius: "rounded-lg" }}
                className={`p-4 ${
                  addrecipient?.recipientInput?.step3?.swiftOrIbanNumber &&
                  !addrecipient?.recipientInput?.step3?.swiftIbanVerified
                    ? "hover:bg-blue-600"
                    : ""
                } w-[150px]`}
                onClickHandler={onVerifySwiftOrIban}
                disabled={
                  addrecipient?.recipientInput?.step3?.swiftIbanLoading ||
                  !addrecipient?.recipientInput?.step3?.swiftOrIbanNumber ||
                  addrecipient?.recipientInput?.step3?.swiftIbanVerified
                }
                isLoading={addrecipient?.recipientInput?.step3?.swiftIbanLoading}
              >
                <div className="flex items-center gap-1">
                  {addrecipient?.recipientInput?.step3?.swiftIbanVerified ? "Verified" : "Verify"}
                </div>
              </Button>
            </div>
            <span className="w-[800px] text-sm" style={{ color: "red" }}>
              {addrecipient?.recipientInput?.step3?.swiftIbanError}
            </span>

            {addrecipient?.recipientInput?.step3?.swiftIbanVerified && (
              <div
                onClick={() => {
                  dispatch(
                    remittanceActions.setAddRecipientInput({
                      step3: {
                        ...addrecipient?.recipientInput?.step3,
                        swiftIbanVerified: false,
                      },
                    })
                  );
                }}
                className="w-[820px] text-blue-600 cursor-pointer text-sm mt-3 ml-1 hover:text-blue-700 underline"
              >
                Edit Field
              </div>
            )}

            {addrecipient?.recipientInput?.step3?.swiftIbanVerified && (
              <>
                <div className="w-[820px] h-px bg-gray-200 my-6" />

                <div className="flex gap-5 mt-0">
                  <div>
                    <div className="w-[400px]">
                      <span className="block text-black-100 text-left text-[15px]">
                        Bank Account Number
                        <span className="text-red-100 text-sm">*</span>
                      </span>
                    </div>
                    <DebounceInput
                      className={
                        "w-[400px] rounded-md h-14 p-4 bg-[#fff] border text-base outline-none"
                      }
                      name="bankAccNo"
                      type="text"
                      value={addrecipient?.recipientInput?.step3?.bankAccNo}
                      debounceTimeout={0}
                      onChange={(e: any) => {
                        const value = e?.target?.value?.trim() || "";
                        dispatch(
                          remittanceActions.setAddRecipientInput({
                            step3: {
                              ...addrecipient?.recipientInput?.step3,
                              bankAccNo: value,
                            },
                          })
                        );
                      }}
                      fullWidth
                      autoComplete="off"
                      inputClasses="text-[20px] bg-blue-100"
                      disabled={false}
                      placeholder={""}
                    />
                  </div>
                  <div className="flex flex-col">
                    <div className="w-[400px]">
                      <span className="block text-black-100 text-left text-[15px]">
                        Confirm Bank Account Number
                        <span className="text-red-100 text-sm">*</span>
                      </span>
                    </div>
                    <DebounceInput
                      className={
                        "w-[400px] rounded-md h-14 p-4 bg-[#fff] border text-base outline-none"
                      }
                      name="confirmBankAccNo"
                      type="text"
                      value={addrecipient?.recipientInput?.step3?.confirmBankAccNo}
                      debounceTimeout={0}
                      onChange={(e: any) => {
                        const value = e?.target?.value?.trim() || "";
                        dispatch(
                          remittanceActions.setAddRecipientInput({
                            step3: {
                              ...addrecipient?.recipientInput?.step3,
                              confirmBankAccNo: value,
                            },
                          })
                        );
                      }}
                      autoComplete="off"
                      fullWidth
                      inputClasses="text-[20px] bg-blue-100"
                      disabled={false}
                      placeholder={""}
                    />
                    {addrecipient?.recipientInput?.step3?.confirmBankAccNo !==
                      addrecipient?.recipientInput?.step3?.bankAccNo &&
                      addrecipient?.recipientInput?.step3?.confirmBankAccNo && (
                        <span className="text-red-500 text-[14px] ml-3">
                          The account numbers do not match.
                        </span>
                      )}
                  </div>
                </div>

                <div className="flex gap-5 mt-6">
                  <div>
                    <div className="w-[400px]">
                      <span className="block text-black-100 text-left text-[15px]">
                        Bank Account Country
                        {/* <span className="text-red-100 text-sm">*</span> */}
                      </span>
                    </div>
                    <SelectDropdown
                      data={AppConstants.CountryOfResidence}
                      value={addrecipient?.recipientInput?.step3?.bankAccCountry}
                      onChange={(response) => {
                        dispatch(
                          remittanceActions.setAddRecipientInput({
                            step3: {
                              ...addrecipient?.recipientInput?.step3,
                              bankAccCountry: response,
                            },
                          })
                        );
                      }}
                      getLabel={(item) => item}
                      getValue={(item) => item}
                      width={"400px"}
                      placeHolderFontSize={"16px"}
                      placeHolderColor={"#a6a6a6"}
                      textSize={"16px"}
                      fontWeight={400}
                      backgroundColor="#fff"
                      isSearchable={true}
                      isLoading={false}
                    />
                  </div>
                  <div>
                    <div className="w-[400px]">
                      <span className="block text-black-100 text-left text-[15px]">
                        Bank Name
                        <span className="text-red-100 text-sm">*</span>
                      </span>
                    </div>
                    <DebounceInput
                      className={
                        "w-[400px] rounded-md h-14 p-4 bg-[#fff] border text-base outline-none"
                      }
                      name="bankName"
                      type="text"
                      value={addrecipient?.recipientInput?.step3?.bankName}
                      debounceTimeout={0}
                      onChange={(e: any) => {
                        const value = e?.target?.value?.trim() || "";
                        dispatch(
                          remittanceActions.setAddRecipientInput({
                            step3: {
                              ...addrecipient?.recipientInput?.step3,
                              bankName: value,
                            },
                          })
                        );
                      }}
                      autoComplete="off"
                      fullWidth
                      inputClasses="text-[20px] bg-blue-100"
                      disabled={false}
                      placeholder={""}
                    />
                  </div>
                </div>

                <div className="flex gap-5 mt-6">
                  <div>
                    <div className="w-[400px]">
                      <span className="block text-black-100 text-left text-[15px]">
                        {addrecipient?.recipientInput?.step3?.sortBSBTransitRoutingPlaceholder}
                        {addrecipient?.recipientInput?.step3?.sortBSBTransitRoutingPlaceholder !==
                          "Sort/BSB/Transit/Routing(Optional)" && (
                          <span className="text-red-100 text-sm">*</span>
                        )}
                      </span>
                    </div>
                    <DebounceInput
                      className={
                        "w-[400px] rounded-md h-14 p-4 bg-[#fff] border text-base outline-none"
                      }
                      name="sortBSBTransitRouting"
                      type="text"
                      value={addrecipient?.recipientInput?.step3?.sortBSBTransitRouting}
                      debounceTimeout={0}
                      onChange={(e: any) => {
                        const value = e?.target?.value?.trim() || "";
                        dispatch(
                          remittanceActions.setAddRecipientInput({
                            step3: {
                              ...addrecipient?.recipientInput?.step3,
                              sortBSBTransitRouting: value,
                            },
                          })
                        );
                      }}
                      fullWidth
                      autoComplete="off"
                      inputClasses="text-[20px] bg-blue-100"
                      disabled={
                        addrecipient?.recipientInput?.step3?.sortBSBTransitRoutingPlaceholder ===
                        "Sort/BSB/Transit/Routing(Optional)"
                      }
                      placeholder={""}
                    />
                  </div>
                  <div>
                    <div className="w-[400px]">
                      <span className="block text-black-100 text-left text-[15px]">
                        Reference ID
                        <span className="text-red-100 text-sm"></span>
                      </span>
                    </div>
                    <DebounceInput
                      className={
                        "w-[400px] rounded-md h-14 p-4 bg-[#fff] border text-base outline-none"
                      }
                      name="referenceId"
                      type="text"
                      value={addrecipient?.recipientInput?.step3?.referenceId}
                      debounceTimeout={0}
                      onChange={(e: any) => {
                        const value = e?.target?.value?.trim() || "";
                        dispatch(
                          remittanceActions.setAddRecipientInput({
                            step3: {
                              ...addrecipient?.recipientInput?.step3,
                              referenceId: value,
                            },
                          })
                        );
                      }}
                      autoComplete="off"
                      fullWidth
                      inputClasses="text-[20px] bg-blue-100"
                      disabled={false}
                      placeholder={""}
                    />
                  </div>
                </div>

                <div className="flex gap-5 mt-6">
                  <div>
                    <div className="w-[820px]">
                      <span className="block text-black-100 text-left text-[15px]">
                        Bank Address
                        <span className="text-red-100 text-sm"></span>
                      </span>
                    </div>
                    <DebounceInput
                      className={
                        "w-[820px] rounded-md h-14 p-4 bg-[#fff] border text-base outline-none"
                      }
                      name="bankAddress"
                      type="text"
                      value={addrecipient?.recipientInput?.step3?.bankAddress}
                      debounceTimeout={0}
                      onChange={(e: any) => {
                        const value = e?.target?.value?.trim() || "";
                        dispatch(
                          remittanceActions.setAddRecipientInput({
                            step3: {
                              ...addrecipient?.recipientInput?.step3,
                              bankAddress: value,
                            },
                          })
                        );
                      }}
                      fullWidth
                      autoComplete="off"
                      inputClasses="text-[20px] bg-blue-100"
                      disabled={false}
                      placeholder={""}
                    />
                  </div>
                </div>

                <div className="flex gap-5 mt-6">
                  <div>
                    <div className="w-[300px]">
                      <span className="block text-black-100 text-left text-[15px]">
                        Unit Number
                      </span>
                    </div>
                    <DebounceInput
                      className={
                        "w-[300px] rounded-md h-14 p-4 bg-[#fff] border text-base outline-none"
                      }
                      name="unitNumber"
                      type="text"
                      value={addrecipient?.recipientInput?.step3?.unitNumber}
                      debounceTimeout={0}
                      onChange={(e: any) => {
                        const value = e?.target?.value?.trim() || "";
                        dispatch(
                          remittanceActions.setAddRecipientInput({
                            step3: {
                              ...addrecipient?.recipientInput?.step3,
                              unitNumber: value,
                            },
                          })
                        );
                      }}
                      fullWidth
                      autoComplete="off"
                      inputClasses="text-[20px] bg-blue-100"
                      disabled={false}
                      placeholder={""}
                    />
                  </div>
                  <div className="flex flex-col w-[500px] justify-center mt-5">
                    <div className="relative flex items-center w-full space-x-3 group">
                      <Checkbox
                        label="Click if your recipient has a corresponding account"
                        value={addrecipient?.recipientInput?.step3?.correspondingAccount}
                        handleChange={() => {
                          dispatch(
                            remittanceActions.setAddRecipientInput({
                              step3: {
                                ...addrecipient?.recipientInput?.step3,
                                correspondingAccount:
                                  !addrecipient?.recipientInput?.step3?.correspondingAccount,
                              },
                            })
                          );
                        }}
                      />
                      <div className="transition-all cursor-pointer hover:scale-105">
                        <InfoIcon />
                      </div>
                      <div className="absolute flex-col items-center justify-center hidden px-5 w-[500px] text-[14px] py-4 mt-[160px] font-semibold text-white rounded-lg shadow-xl bg-primary group-hover:flex animate-fade">
                        <span className="font-bold text-[16px]">
                          What is a corresponding account?
                        </span>
                        <span>
                          A correspondent account is a bank account set up to handle transactions
                          for another financial institution.
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            <div className="flex items-center justify-center align-middle mt-8 gap-5 mb-10">
              <Button
                bgColor={"bg-white"}
                fontStyle={{
                  fontFamily: "font-Inter",
                  fontSize: "text-sm",
                  fontWeight: "font-bold",
                  fontColor: "text-white",
                }}
                borderStyle={{
                  borderRadius: "rounded-lg",
                  borderWidth: "border",
                  borderColor: "border-gray-300",
                }}
                className={"p-4 hover:bg-gray-50 w-[10rem] h-12"}
                onClickHandler={() => {
                  dispatch(remittanceActions.setRecipientStepNumber(1));
                }}
                disabled={false}
                isLoading={false}
              >
                <div className="flex items-center justify-center gap-1 text-blue-600">
                  {"Go Back"}
                </div>
              </Button>
              <Button
                bgColor={step3Disable ? "bg-gray-300" : "bg-[#006EFF]"}
                fontStyle={{
                  fontFamily: "font-Inter",
                  fontSize: "text-sm",
                  fontWeight: "font-bold",
                  fontColor: "text-white",
                }}
                borderStyle={{ borderRadius: "rounded-lg" }}
                className={`p-4 ${!step3Disable ? "hover:bg-blue-600" : ""} w-[10rem]`}
                onClickHandler={() => {
                  if (addrecipient?.recipientInput?.step3?.correspondingAccount) {
                    dispatch(remittanceActions.setRecipientStepNumber(3));
                  } else {
                    handleAddRecipient();
                  }
                }}
                disabled={step3Disable || addrecipient?.addRecipientSubmit?.loading}
                isLoading={addrecipient?.addRecipientSubmit?.loading}
              >
                <div className="flex items-center gap-1">{"Proceed"}</div>
              </Button>
            </div>
          </div>
        )}

        {/* Step 4 */}
        {addrecipient?.recipientStepNumber == 3 && (
          <div className="flex items-center justify-center flex-col mt-10">
            <div className="flex gap-5 mt-0">
              <div>
                <div className="w-[400px]">
                  <span className="block text-black-100 text-left text-[15px]">
                    Bank Account Number
                    <span className="text-red-100 text-sm">*</span>
                  </span>
                </div>
                <DebounceInput
                  className={
                    "w-[400px] rounded-md h-14 p-4 bg-[#fff] border text-base outline-none"
                  }
                  name="bankAccNo"
                  type="text"
                  value={addrecipient?.recipientInput?.step4?.bankAccNo}
                  debounceTimeout={0}
                  onChange={(e: any) => {
                    const value = e?.target?.value?.trim() || "";
                    dispatch(
                      remittanceActions.setAddRecipientInput({
                        step4: {
                          ...addrecipient?.recipientInput?.step4,
                          bankAccNo: value,
                        },
                      })
                    );
                  }}
                  fullWidth
                  autoComplete="off"
                  inputClasses="text-[20px] bg-blue-100"
                  disabled={false}
                  placeholder={""}
                />
              </div>
              <div className="flex flex-col">
                <div className="w-[400px]">
                  <span className="block text-black-100 text-left text-[15px]">
                    Confirm Bank Account Number
                    <span className="text-red-100 text-sm">*</span>
                  </span>
                </div>
                <DebounceInput
                  className={
                    "w-[400px] rounded-md h-14 p-4 bg-[#fff] border text-base outline-none"
                  }
                  name="confirmBankAccNo"
                  type="text"
                  value={addrecipient?.recipientInput?.step4?.confirmBankAccNo}
                  debounceTimeout={0}
                  onChange={(e: any) => {
                    const value = e?.target?.value?.trim() || "";
                    dispatch(
                      remittanceActions.setAddRecipientInput({
                        step4: {
                          ...addrecipient?.recipientInput?.step4,
                          confirmBankAccNo: value,
                        },
                      })
                    );
                  }}
                  autoComplete="off"
                  fullWidth
                  inputClasses="text-[20px] bg-blue-100"
                  disabled={false}
                  placeholder={""}
                />
                {addrecipient?.recipientInput?.step4?.confirmBankAccNo !==
                  addrecipient?.recipientInput?.step4?.bankAccNo &&
                  addrecipient?.recipientInput?.step4?.confirmBankAccNo && (
                    <span className="text-red-500 text-[14px] ml-3">
                      The account numbers do not match.
                    </span>
                  )}
              </div>
            </div>

            <div className="flex gap-5 mt-6">
              <div>
                <div className="w-[820px]">
                  <span className="block text-black-100 text-left text-[15px]">
                    Bank Name
                    <span className="text-red-100 text-sm">*</span>
                  </span>
                </div>
                <DebounceInput
                  className={
                    "w-[820px] rounded-md h-14 p-4 bg-[#fff] border text-base outline-none"
                  }
                  name="bankAddress"
                  type="text"
                  value={addrecipient?.recipientInput?.step4?.bankName}
                  debounceTimeout={0}
                  onChange={(e: any) => {
                    const value = e?.target?.value?.trim() || "";
                    dispatch(
                      remittanceActions.setAddRecipientInput({
                        step4: {
                          ...addrecipient?.recipientInput?.step4,
                          bankName: value,
                        },
                      })
                    );
                  }}
                  fullWidth
                  autoComplete="off"
                  inputClasses="text-[20px] bg-blue-100"
                  disabled={false}
                  placeholder={""}
                />
              </div>
            </div>

            <div className="flex items-center justify-center align-middle mt-8 gap-5 mb-10">
              <Button
                bgColor={"bg-white"}
                fontStyle={{
                  fontFamily: "font-Inter",
                  fontSize: "text-sm",
                  fontWeight: "font-bold",
                  fontColor: "text-white",
                }}
                borderStyle={{
                  borderRadius: "rounded-lg",
                  borderWidth: "border",
                  borderColor: "border-gray-300",
                }}
                className={"p-4 hover:bg-gray-50 w-[10rem] h-12"}
                onClickHandler={() => {
                  dispatch(remittanceActions.setRecipientStepNumber(2));
                  dispatch(
                    remittanceActions.setAddRecipientInput({
                      step3: {
                        ...addrecipient?.recipientInput?.step3,
                        correspondingAccount: false,
                      },
                      step4: {
                        ...addrecipient?.recipientInput?.step4,
                        bankAccNo: "",
                        confirmBankAccNo: "",
                        bankName: "",
                      },
                    })
                  );
                }}
                disabled={false}
                isLoading={false}
              >
                <div className="flex items-center justify-center gap-1 text-blue-600">
                  {"Go Back"}
                </div>
              </Button>
              <Button
                bgColor={step4Disable ? "bg-gray-300" : "bg-[#006EFF]"}
                fontStyle={{
                  fontFamily: "font-Inter",
                  fontSize: "text-sm",
                  fontWeight: "font-bold",
                  fontColor: "text-white",
                }}
                borderStyle={{ borderRadius: "rounded-lg" }}
                className={`p-4 ${!step4Disable ? "hover:bg-blue-600" : ""} w-[10rem]`}
                onClickHandler={handleAddRecipient}
                disabled={step4Disable || addrecipient?.addRecipientSubmit?.loading}
                isLoading={addrecipient?.addRecipientSubmit?.loading}
              >
                <div className="flex items-center gap-1">{"Proceed"}</div>
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default RemittanceAddRecipient;
